import { useUniclientTrackingContext } from '@lemonade-hq/uniclient-app';
import type { PageSection, UniclientPageProps } from '@lemonade-hq/uniclient-chat';
import {
  Card,
  Flex,
  Icon,
  IconButton,
  Layout,
  spacing,
  Text,
  useObserveElementVisibility,
  useResponsiveValue,
  useViewportSize,
} from '@lemonade-hq/uniclient-theme';
import { clsx } from 'clsx';
import { motion } from 'motion/react';
import type { RefObject } from 'react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { getPageAnimation } from '../animators/pageAnimation';
import { InterpolableText } from '../elements/InterpolableText';
import { useInitialHeight } from '../hooks/useInitialHeight';
import { Background } from './components/Background/Background';
import { HeroCard } from './components/HeroCard/HeroCard';
import { DynamicComponent } from './DynamicComponent';
import type { UniclientComponentAdapterProps } from './types';
import * as styles from './WizardPageAdapter.css';

export type WizardPageAdapterProps = UniclientComponentAdapterProps<UniclientPageProps>;

type SectionsProps = Omit<WizardPageAdapterProps, 'componentProps'> &
  PageSection & {
    readonly entranceAnimationComplete: RefObject<boolean>;
    readonly hasSmallerPadding: boolean;
    readonly alignToTop: boolean;
  };

// won't be wrapped with card on all platforms
const COMPONENTS_EXCLUDED_FROM_CARD_SM = ['selectable_cards', 'accordion', 'collapsible', 'illustrated_cards'];
// won't be wrapped with card on tablet and desktop
const COMPONENTS_EXCLUDED_FROM_CARD_MD = [...COMPONENTS_EXCLUDED_FROM_CARD_SM];
// won't be wrapped with card on desktop
const COMPONENTS_EXCLUDED_FROM_CARD_LG = [...COMPONENTS_EXCLUDED_FROM_CARD_MD, 'grid'];

function getIsComponentExcludedFromCardBasedOnViewport(
  componentType: string,
  isAboveDesktopViewport: boolean,
  isTabletViewport: boolean,
): boolean {
  if (isAboveDesktopViewport) return COMPONENTS_EXCLUDED_FROM_CARD_LG.includes(componentType);
  if (isTabletViewport) return COMPONENTS_EXCLUDED_FROM_CARD_MD.includes(componentType);

  return COMPONENTS_EXCLUDED_FROM_CARD_SM.includes(componentType);
}
const Section: React.FC<SectionsProps> = ({
  background,
  alignToTop,
  title,
  description,
  descriptionType,
  id,
  answers,
  answersSchema,
  onAction,
  disabled,
  animationDelay: _animationDelay,
  footer,
  tracking,
  entranceAnimationComplete,
  hasSmallerPadding,
  ...adapterProps
}) => {
  const { isAboveDesktopViewport, isTabletViewport } = useViewportSize();
  const sectionRef = React.useRef<HTMLDivElement>();
  const trackedEvent = useRef(false);
  const intersectionEntry = useObserveElementVisibility({
    ref: sectionRef,
    threshold: 0.2,
    enabled: tracking != null && !trackedEvent.current,
  });
  const { trackEvent } = useUniclientTrackingContext();

  useEffect(() => {
    if (tracking == null) return;
    if (entranceAnimationComplete.current === true && !trackedEvent.current && intersectionEntry?.isIntersecting) {
      trackEvent('section.viewed', tracking);
      trackedEvent.current = true;
    }
  }, [intersectionEntry, tracking, entranceAnimationComplete, trackEvent]);

  const type = useResponsiveValue({
    sm: 'secondary',
    lg: background,
  });

  const shouldBeWrappedWithCard =
    type === 'secondary' &&
    adapterProps.component != null &&
    !Array.isArray(adapterProps.component) &&
    !getIsComponentExcludedFromCardBasedOnViewport(
      adapterProps.component.type,
      isAboveDesktopViewport,
      isTabletViewport,
    );

  const pageEntranceAnimation = useMemo(() => getPageAnimation(), []);
  const isTitleObject = typeof title === 'object';
  const titleText = isTitleObject ? title.text : title;
  const hasIcon = isTitleObject && 'icon' in title;

  const hasDescriptionIcon = typeof description === 'object' && 'icon' in description;
  const descriptionText = hasDescriptionIcon ? description.text : description;

  return (
    <Flex
      {...pageEntranceAnimation}
      alignItems="center"
      as={motion.div}
      className={clsx(styles.section({ hasSmallerPadding, alignToTop }), {
        [styles.primarySection]: type === 'primary',
        [styles.secondarySection]: type === 'secondary',
      })}
      flexDirection="column"
      gap={spacing.s24}
      id={id}
      layout="position"
      position="relative"
      ref={sectionRef}
      textAlign="center"
    >
      <Layout
        as={motion.div}
        className={styles.sectionBg({
          type,
        })}
        height={alignToTop ? '200%' : '100%'}
        layout
        position="absolute"
        top={alignToTop ? '-100%' : '0'}
        width="100vw"
      />
      {(titleText != null || description != null) && (
        <Flex flexDirection="column" gap={spacing.s08}>
          {titleText != null && (
            <Flex alignItems="center" gap={spacing.s12} justifyContent="center">
              <Text type="h4">
                <InterpolableText answers={answers} onAction={onAction} text={titleText} variant="inherit" />
              </Text>
              {hasIcon && (
                <IconButton aria-label={title.icon} icon={title.icon} onClick={() => void onAction?.(title.action)} />
              )}
            </Flex>
          )}
          {descriptionText != null && (
            <Flex alignItems="center" gap={spacing.s04} justifyContent="center">
              {hasDescriptionIcon && <Icon name={description.icon} />}
              <Text
                color="$textSecondary"
                px={hasDescriptionIcon ? 0 : spacing.s16}
                textAlign="center"
                type={descriptionType === 'small' ? 'bodySmall' : 'bodyMedium'}
              >
                <InterpolableText
                  answers={answers}
                  dangerous
                  onAction={onAction}
                  text={descriptionText}
                  variant="inherit"
                />
              </Text>
            </Flex>
          )}
        </Flex>
      )}
      {adapterProps.component != null ? (
        shouldBeWrappedWithCard ? (
          <Card borderRadius="lg" data-animated-disabled gap={spacing.s16} p={spacing.s24} shadow="xl" textAlign="left">
            <DynamicComponent
              {...adapterProps}
              answers={answers}
              answersSchema={answersSchema}
              disabled={disabled}
              extraComponentProps={{
                disableAutoFocus: true,
              }}
              id={titleText}
              onAction={onAction}
            />
          </Card>
        ) : (
          <Flex
            alignItems="center"
            flexDirection="column"
            gap={spacing.s16}
            justifyContent="center"
            mt={spacing.s08}
            textAlign="left"
            width="100%"
          >
            <DynamicComponent
              {...adapterProps}
              answers={answers}
              answersSchema={answersSchema}
              disabled={disabled}
              extraComponentProps={{
                disableAutoFocus: true,
              }}
              id={titleText}
              onAction={onAction}
            />
          </Flex>
        )
      ) : null}
      {footer != null && (
        <Text color="$textSecondary" pt={shouldBeWrappedWithCard ? undefined : spacing.s56} type="bodySmall">
          <InterpolableText answers={answers} dangerous onAction={onAction} text={footer} variant="inherit" />
        </Text>
      )}
    </Flex>
  );
};

export const WizardPageAdapter: React.FC<WizardPageAdapterProps> = ({
  componentProps: { sections, hero },
  onAction,
  answers,
  disabled,
  loading,
  animationDelay,
  ...adapterProps
}) => {
  const entranceAnimationCompleted = useRef(false);
  const pageEntranceAnimation = useMemo(
    () => getPageAnimation({ delay: (animationDelay ?? 0) / 1000 }),
    [animationDelay],
  );

  const onAnimationComplete = useCallback(() => {
    entranceAnimationCompleted.current = true;
  }, []);

  const { elementRef, height } = useInitialHeight(entranceAnimationCompleted.current);

  return (
    <Flex
      {...pageEntranceAnimation}
      as={motion.div}
      data-animated-disabled
      data-type="page"
      flexDirection="column"
      mx={{
        sm: `-${spacing.s16}`, // Designers requested to have a smaller margins in page frame type (currently affects quote page)
        md: 0,
      }}
      onAnimationComplete={onAnimationComplete}
      position="relative"
    >
      {hero != null && (
        <>
          {hero.background != null && <Background background={hero.background} cardHeight={height} />}
          <Flex
            alignSelf={{
              sm: 'center',
              md: !hero.wrapWithCard ? 'flex-start' : 'center',
            }}
            flexDirection="column"
            maxWidth={{
              md: styles.HERO_MAX_WIDTH,
            }}
            pb={{
              sm: spacing.s20,
              lg: 0,
            }}
            ref={elementRef}
            width="100%"
            zIndex={hero.wrapWithCard ? 1 : 0}
          >
            {hero.wrapWithCard && (
              <HeroCard
                action={hero.action}
                adapterProps={adapterProps}
                answers={answers}
                coInsurance={hero.coInsurance}
                component={hero.component}
                disabled={disabled}
                disclaimer={hero.disclaimer}
                id={hero.id}
                loading={loading}
                mainButtonExplainer={hero.mainButtonExplainer}
                onAction={onAction}
                secondaryAction={hero.secondaryAction}
              />
            )}
            {!hero.wrapWithCard && (
              <DynamicComponent
                {...adapterProps}
                answers={answers}
                component={hero.component}
                disabled={disabled}
                extraComponentProps={{
                  hasLargerPadding: true,
                }}
                onAction={onAction}
              />
            )}
          </Flex>
        </>
      )}
      {sections != null && (
        <Layout pt={{ lg: hero == null ? undefined : !hero.wrapWithCard ? spacing.s24 : spacing.s80 }}>
          {sections.map((section, index) => (
            <Section
              alignToTop={index === 0 && hero == null}
              disabled={disabled}
              hasSmallerPadding={index === 0 && !hero?.wrapWithCard}
              loading={loading}
              {...adapterProps}
              {...section}
              animationDelay={animationDelay}
              answers={answers}
              entranceAnimationComplete={entranceAnimationCompleted}
              key={section.id ?? `section-${index}`}
              onAction={onAction}
            />
          ))}
        </Layout>
      )}
    </Flex>
  );
};
